import React, { useEffect, useRef } from "react";
import { object, number } from 'yup';

import { TailwindInput } from "../../Tailwind";
import { useUpdateFieldValue } from "../../../shared/hooks/mutations/useUpdateFieldValue";
import { updateFieldValue } from "../../../shared/api/fields";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";


export default function FloatField({ name, field }) {
    // Gérer le cas où currentValue est undefined (nouveau model data)
    const safeCurrentValue = field.currentValue?.value ?? field.value?.value ?? '';

    const [fieldValue, setFieldValue] = React.useState(safeCurrentValue);
    const [initialValue, setInitialValue] = React.useState(safeCurrentValue);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();
    const updateField = useUpdateFieldValue();
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId);

    // Refs for saving unsaved changes on unmount
    const fieldValueRef = useRef(fieldValue);
    const initialValueRef = useRef(initialValue);
    const fieldIdRef = useRef(field.id);
    const isSavingRef = useRef(false);
    fieldValueRef.current = fieldValue;
    initialValueRef.current = initialValue;
    fieldIdRef.current = field.id;

    // Save unsaved changes when the component unmounts (e.g. selecting another model_data)
    useEffect(() => {
        return () => {
            if (!isSavingRef.current && fieldValueRef.current != initialValueRef.current) {
                const val = String(fieldValueRef.current);
                updateFieldValue(fieldIdRef.current, val !== "" ? val.replace(',', '.') : null);
            }
        };
    }, []);

    useEffect(() => {
        const newValue = field.currentValue?.value ?? field.value?.value ?? '';
        setFieldValue(newValue);
    }, [field.currentValue?.value, field.value?.value])

    const schema = object({
        value: number().transform((_, value) => {
            return +value.replace(/,/, '.');
        })
    })

    const changeValue = (event: any) => {
        field.compute_error = null;
        if (schema.isValidSync({value: event.target.value}) || !event.target.value.length) {
            if (field.positive_only == true && event.target.value < 0) {
                field.error = "Should be positive."
            }
            else {
                field.error = null;
            }
        }
        else {
            field.error = "Should be a Float."
        }

        const newValue = event.target.value;
        if (field.currentValue) {
            field.currentValue.value = newValue;
        }
        setFieldValue(newValue);
    }

    const validate = (event: any) => {
        const newValue = event.target.value;
        const currentVal = field.currentValue?.value ?? field.value?.value ?? '';

        if (currentVal != initialValue)
        {
            isSavingRef.current = true;
            triggerSave();
            updateField.mutate(
                { id: field.id, value: newValue !== "" ? newValue.replace(',', '.') : null, scenarioId: scenarioId ?? 0 },
                {
                    onSuccess: () => {
                        setInitialValue(newValue);
                        resolveSave();
                        isSavingRef.current = false;
                    },
                    onError: () => {
                        isSavingRef.current = false;
                    },
                }
            );
        }
    }

    return (
        <div className="flex flex-row items-center mb-1">
            <TailwindInput
                type="number"
                step="any"
                value={fieldValue}
                placeholder={name}
                onChange={(e) => changeValue(e)}
                onBlur={(e) => validate(e)}
                error={field.compute_error || field.error}
                helperText={field.compute_error || field.error}
                suffix={field.suffix}
                readOnly={isReadOnly}
                className="h-10 w-[200px]"
            />
            <SaveIndicator status={saveStatus} className="ml-2" />
            <div className="flex-grow"/>
        </div>
    )
}
